import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductRailNavRefs
 * @property {HTMLElement} [dots] - The host the page dots are drawn into
 * @property {HTMLElement} [counter] - The "page / pages" readout
 * @property {HTMLButtonElement} [previous] - Steps back one page
 * @property {HTMLButtonElement} [next] - Steps forward one page
 */

/**
 * Pages a horizontal rail: one dot per viewport of items, plus an optional
 * counter and prev/next pair.
 *
 * The rail is the host's own scroller, so this reads it rather than driving it —
 * a page is one scroller width, which is what the mockup counts. Dots are built
 * here because the page count depends on how many items fit.
 *
 * @extends {Component<ProductRailNavRefs>}
 */
export class ProductRailNav extends Component {
  requiredRefs = ['dots'];

  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {HTMLButtonElement[]} */
  #dots = [];

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();

    // Horizon's product rail scrolls a <slideshow-slides>; other hosts name their own scroller
    this.#scroller = /** @type {HTMLElement | null} */ (
      this.querySelector(this.dataset.scroller || 'slideshow-slides')
    );
    if (!this.#scroller) return;

    this.#scroller.addEventListener('scroll', this.#handleScroll, { passive: true });

    this.#resizeObserver = new ResizeObserver(() => this.#update());
    this.#resizeObserver.observe(this.#scroller);

    this.refs.previous?.addEventListener('click', this.#handlePrevious);
    this.refs.next?.addEventListener('click', this.#handleNext);

    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#scroller?.removeEventListener('scroll', this.#handleScroll);
    this.refs.previous?.removeEventListener('click', this.#handlePrevious);
    this.refs.next?.removeEventListener('click', this.#handleNext);
    this.#resizeObserver?.disconnect();
    clearTimeout(this.#timer);
  }

  /** Re-measure once the section has been morphed back in. */
  updatedCallback() {
    super.updatedCallback();
    this.#update();
  }

  #handleScroll = () => {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#update(), 90);
  };

  #handlePrevious = () => this.#goToPage(this.#currentPage - 2);

  #handleNext = () => this.#goToPage(this.#currentPage);

  /** @returns {number} The width of one page of items. */
  get #pageWidth() {
    return Math.max(this.#scroller?.clientWidth ?? 1, 1);
  }

  /** @returns {number} How many pages the rail holds. */
  get #pageCount() {
    return Math.max(1, Math.ceil((this.#scroller?.scrollWidth ?? 0) / this.#pageWidth));
  }

  /** @returns {number} The 1-based page currently in view. */
  get #currentPage() {
    return Math.min(this.#pageCount, Math.round((this.#scroller?.scrollLeft ?? 0) / this.#pageWidth) + 1);
  }

  #update() {
    const { dots, counter, previous, next } = this.refs;
    if (!this.#scroller || !dots) return;

    // A rail in a hidden tab panel measures 0 wide, which would page it into the thousands
    if (!this.#scroller.clientWidth) return;

    const pages = this.#pageCount;
    const page = this.#currentPage;

    this.#buildDots(pages);

    for (const [index, dot] of this.#dots.entries()) {
      dot.setAttribute('aria-current', String(index === page - 1));
    }

    if (counter) counter.textContent = `${page} / ${pages}`;

    if (previous) previous.disabled = page <= 1;
    if (next) next.disabled = page >= pages;

    // A rail that fits in one viewport has nothing to page through
    const nav = dots.closest('.rail-nav');
    nav?.classList.toggle('rail-nav--paged', pages > 1);
  }

  /** @param {number} pages */
  #buildDots(pages) {
    const { dots } = this.refs;
    if (!dots || this.#dots.length === pages) return;

    dots.replaceChildren();
    this.#dots = [];

    const label = Theme.translations.go_to_page ?? 'Go to page {{ index }}';

    for (let index = 0; index < pages; index++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'rail-nav__dot';
      dot.setAttribute('aria-label', label.replace('{{ index }}', String(index + 1)));
      dot.addEventListener('click', () => this.#goToPage(index));
      dots.append(dot);
      this.#dots.push(dot);
    }
  }

  /** @param {number} index */
  #goToPage(index) {
    const target = Math.min(Math.max(index, 0), this.#pageCount - 1);

    this.#scroller?.scrollTo({
      left: target * this.#pageWidth,
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }
}

if (!customElements.get('product-rail-nav')) {
  customElements.define('product-rail-nav', ProductRailNav);
}
